import type { PropertyValueResource } from "@octopusdeploy/octopus-server-client";
import { useState } from "react";

type Status = "new" | "deleted";

type ProjectTenantValue = {
    value: PropertyValueResource | undefined;
    status?: Status;
};

export function useProjectTenantVariableValueChangeTracker(): ProjectTenantVariableValueChangeTracker {
    const [changes, setChanges] = useState<Map<ProjectTenantVariableValueKey, ProjectTenantValue>>(new Map());

    return {
        has: (key: ProjectTenantVariableValueKey) => changes.has(key),
        get: (key: ProjectTenantVariableValueKey) => {
            if (changes.has(key)) {
                return changes.get(key)?.value;
            }

            throw new Error(`Key "${key}" not found in changed project tenant variables`);
        },
        set: (key: ProjectTenantVariableValueKey, value: PropertyValueResource | undefined) => {
            const copy = new Map(changes);
            const existingValue = copy.get(key);

            copy.set(key, { value: value, status: existingValue?.status });
            setChanges(copy);
        },
        add: (key: ProjectTenantVariableValueKey, value: PropertyValueResource | undefined) => {
            const copy = new Map(changes);
            copy.set(key, { value, status: "new" });

            setChanges(copy);
        },
        delete: (key: ProjectTenantVariableValueKey) => {
            const tenantValue = changes.get(key);
            const copy = new Map(changes);

            if (tenantValue?.status === "new") {
                copy.delete(key);
            } else {
                copy.set(key, { value: undefined, status: "deleted" });
            }

            setChanges(copy);
        },
        remove: (key: ProjectTenantVariableValueKey) => {
            const copy = new Map(changes);
            copy.delete(key);

            setChanges(copy);
        },
        reset: () => setChanges(new Map()),
        values: () =>
            Array.from(changes.entries()).map(([key, tenantValue]) => {
                const { projectId, templateId, tenantId, environmentId, guid: id } = extractProjectTenantVariableIds(key);
                return {
                    id,
                    projectId,
                    templateId,
                    tenantId,
                    environmentId,
                    value: tenantValue?.value,
                    status: tenantValue?.status,
                };
            }),
        hasAny: () => changes.size > 0,
        changedTenants: () => {
            const tenantIds: Set<string> = new Set();
            for (const variableKey of changes.keys()) {
                const { tenantId } = extractProjectTenantVariableIds(variableKey);
                tenantIds.add(tenantId);
            }

            return tenantIds;
        },
        newVariables: (projectId: string, templateId: string, tenantId: string) => {
            const result = new Map<ProjectTenantVariableValueKey, PropertyValueResource | undefined>();
            for (const [key, value] of changes) {
                const { projectId: keyProjectId, templateId: keyTemplateId, tenantId: keyTenantId } = extractProjectTenantVariableIds(key);
                if (keyProjectId === projectId && keyTemplateId === templateId && keyTenantId === tenantId && value?.status === "new") {
                    result.set(key, value.value);
                }
            }
            return result;
        },
        removedVariables: () => {
            const result = new Set<ProjectTenantVariableValueKey>();
            for (const [key, value] of changes) {
                if (value?.status === "deleted") {
                    result.add(key);
                }
            }
            return result;
        },
    };
}

export interface ProjectTenantVariableValueChangeTracker {
    has: (key: ProjectTenantVariableValueKey) => boolean;
    get: (key: ProjectTenantVariableValueKey) => PropertyValueResource | undefined; // To satisfy type check. Notice that PropertyValueResource can be undefined
    set: (key: ProjectTenantVariableValueKey, value: PropertyValueResource | undefined) => void;
    add: (key: ProjectTenantVariableValueKey, value: PropertyValueResource | undefined) => void;
    delete: (key: ProjectTenantVariableValueKey) => void;
    remove: (key: ProjectTenantVariableValueKey) => void;
    reset: () => void;
    values: () => ChangedProjectTemplateVariableValue[];
    hasAny: () => boolean;
    changedTenants: () => Set<string>;
    newVariables: (projectId: string, templateId: string, tenantId: string) => Map<ProjectTenantVariableValueKey, PropertyValueResource | undefined>;
    removedVariables: () => Set<ProjectTenantVariableValueKey>;
}

export interface ChangedProjectTemplateVariableValue {
    id: string;
    projectId: string;
    templateId: string;
    tenantId: string;
    environmentId: string;
    value: PropertyValueResource | undefined;
    status: Status | undefined;
}

export type ProjectTenantVariableValueKey = `${string};${string};${string};${string};${string}`;

export function createProjectTenantVariableKey(projectId: string, templateId: string, tenantId: string, environmentId: string, guid: string): ProjectTenantVariableValueKey {
    return `${projectId};${templateId};${tenantId};${environmentId};${guid}`;
}

export function extractProjectTenantVariableIds(key: ProjectTenantVariableValueKey): { projectId: string; templateId: string; tenantId: string; environmentId: string; guid: string } {
    const extracted = key.split(`;`);
    return {
        projectId: extracted[0],
        templateId: extracted[1],
        tenantId: extracted[2],
        environmentId: extracted[3],
        guid: extracted[4],
    };
}
